import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { type LexicalEditor } from 'lexical'
import { type EffectiveTextStyle } from '@backend/lib/db/typeRoots/layoutNode'

/** Snapshot the active Lexical editor publishes for the workspace toolbar to
 *  read. When a range selection is live, `selectionStyle` reflects the style
 *  at the start of the range, so the toolbar can show meaningful values; the
 *  caller still uses `hasRangeSelection` to decide whether to route updates
 *  to the selection or the whole node.
 *
 *  `lastRange` is a snapshot of the most recent non-collapsed range. The
 *  toolbar uses it to restore the selection after a click that took focus
 *  away from the contenteditable (Radix popovers etc. portal out of the
 *  toolbar wrapper, so mousedown preventDefault can't keep focus). */
export type SavedRange = {
  anchorKey: string
  anchorOffset: number
  focusKey: string
  focusOffset: number
}

export type EditorContextValue = {
  editor: LexicalEditor | null
  selectionStyle: EffectiveTextStyle | null
  hasRangeSelection: boolean
  lastRange: SavedRange | null
}

const defaultValue: EditorContextValue = {
  editor: null,
  selectionStyle: null,
  hasRangeSelection: false,
  lastRange: null,
}

const ValueContext = createContext<EditorContextValue>(defaultValue)
const SetterContext = createContext<(updates: Partial<EditorContextValue>) => void>(() => {})

export const EditorContextProvider = ({ children }: { children: ReactNode }) => {
  const [value, setValue] = useState<EditorContextValue>(defaultValue)
  const update = useCallback(
    (updates: Partial<EditorContextValue>) => setValue((prev) => ({ ...prev, ...updates })),
    [],
  )
  const memoValue = useMemo(() => value, [value])
  return (
    <SetterContext.Provider value={update}>
      <ValueContext.Provider value={memoValue}>{children}</ValueContext.Provider>
    </SetterContext.Provider>
  )
}

export const useEditorContext = (): EditorContextValue => useContext(ValueContext)
export const useEditorContextSetter = () => useContext(SetterContext)
